<template>
  <div class="wp-scope">
    <v-tabs v-model="selectedTab" :hide-slider="true" data-id-e2e="workpackageScopeTabs">
      <template v-for="item in tabs" :key="item.id">
        <v-tab class="wp-tab justify-space-between font-weight-bold" :value="item.id">
          <span>{{ item.name }}</span>
        </v-tab>
        <div class="wp-tab__spacer" />
      </template>

      <div class="wp-tab__filler" />
    </v-tabs>

    <v-window v-model="selectedTab" class="wp-tab">
      <v-window-item
        v-for="item in tabs"
        :key="item.id"
        eager
        :value="item.id"
        class="wp-tab__item"
      >
        <PeriodsTab v-if="item.id === 'periods'" />

        <AssortmentGroupsTab
          v-if="item.id === 'agNew' && wpDateSelections.snapshotDate"
          :custom-workpackage="dirtyWorkpackage()"
          :dates-loaded="datesLoaded"
        />

        <template v-if="item.id === 'agOld'">
          <v-row class="wp-scope__form" no-gutters>
            <v-col :cols="9" class="pr-3">
              <AssortmentGroupsSelection
                v-if="wpDateSelections.snapshotDate"
                :workpackage="workpackage"
                :snapshot-date="wpDateSelections.snapshotDate"
                :selected-assortment-group-settings="
                  workpackage.selectedAssortmentGroupSettings || []
                "
              />
            </v-col>

            <v-col class="col-border-left">
              <div class="col-padding">
                <v-card class="pt-0 pb-2 pr-2 pl-2" style="background: none; box-shadow: none">
                  <h3 class="wp-scope__input-heading">
                    {{ tkey('productsAndStoresLabel') }}
                  </h3>
                  <p class="wp-scope__input-text">{{ tkey('importProductDataLabel') }}:</p>
                  <ImportFromPlanogram v-if="datesLoaded" />
                </v-card>

                <WpScopeProducts />
                <WpScopeStores />
              </div>
            </v-col>
          </v-row>
        </template>
      </v-window-item>
    </v-window>

    <v-row class="footer-panel ma-0">
      <!-- FRESH-TODO: Confirm appearance now both things are here. -->
      <v-col v-if="showSetByEndDateToggle">
        <v-checkbox
          v-model="workpackagesStore.useEndDateSelection"
          class="rtls-checkbox mx-2"
          color="primary"
          density="compact"
          hide-details
        >
          <template #label>
            <div>
              {{ tkey('setByEndDate') }}
            </div>
          </template>
        </v-checkbox>
      </v-col>
      <v-col class="d-flex justify-end pa-3 colspan">
        <v-btn
          data-id-e2e="btnCloseWorkpackage"
          class="mr-0 font-weight-bold btn-text"
          :ripple="false"
          variant="text"
          @click="closeScope"
        >
          {{ $t('actions.close') }}
        </v-btn>
        <v-divider class="mr-0 divider" vertical />
        <v-btn
          v-if="showNotImplemented"
          class="mr-2 font-weight-bold btn-text"
          :ripple="false"
          variant="text"
        >
          {{ $t('actions.discardChanges') }}
        </v-btn>
        <v-btn
          id="scope-save"
          data-id-e2e="btnSaveWorkpackage"
          class="mr-2"
          variant="flat"
          min-width="65"
          action
          :disabled="isWorkpackageSetupRunning || !canEditWorkpackageScope || !validate()"
          @click="$emit('save:workpackage')"
        >
          {{ $t('actions.save') }}
        </v-btn>
        <v-tooltip
          :model-value="hasInvalidPeriodIds"
          :disabled="!hasInvalidPeriodIds"
          location="top"
        >
          <template #activator="{ props }">
            <div v-bind="props">
              <v-btn
                data-id-e2e="btnRunWorkpackage"
                :disabled="!canEditWorkpackageScope || isWorkpackageSetupRunning"
                :loading="isWorkpackageSetupRunning"
                variant="flat"
                primary
                min-width="65"
                @click="runWpSetup"
              >
                {{ $t('workpackagePage.runWp') }}
              </v-btn>
            </div>
          </template>
          <span v-if="hasInvalidPeriodIds">{{ tkey('warnings.periodIdWarning') }}</span>
        </v-tooltip>
      </v-col>
    </v-row>

    <WorkpackageModalSyntheticFillByWarning
      ref="unsavedDataModal"
      :value="showSyntheticFillByWarning"
      @cancel="showSyntheticFillByWarning = false"
      @confirm="syntheticWarningChangeToProductCountConfirmed"
    />
  </div>
</template>

<script>
import { mapActions, mapState } from 'pinia';
import { get, every } from 'lodash';
import SIZE_TYPES from '@enums/size-types';
import { useAssortmentGroupsStore } from '@/stores/assortment-groups';
import { useWorkpackagesStore } from '@/stores/workpackages';
import { useContextStore } from '@/stores/context';
import { useWorkpackageProductsStore } from '@/stores/workpackage-products';
import { useInputValidations } from '@/composables/input-validations';
import { useFeature } from '@/composables/use-feature';
import { usePermission } from '@/composables/use-permission';
import { useInternationalization } from '@/composables/use-internationalization';

export default {
  props: {
    isWorkpackageSetupRunning: {
      type: Boolean,
      required: true,
      default: false,
    },
  },

  setup() {
    const { canEditWorkpackageScope } = usePermission();
    const {
      hasSeasonalityEnabled,
      hasPeriodIdsEnabled,
      betaHasMandatoryPeriodIdsEnabled,
      showSetByEndDateToggle,
      useMarkdownAndWasteToCalculateMarginAndPriceEnabled,
      defaultFillAccordingTo,
    } = useFeature();

    const workpackagesStore = useWorkpackagesStore();

    const {
      required,
      isInteger,
      isNumber,
      isLessThan,
      isAlphanumeric,
      isPositive,
      isNotEmpty,
      isGreaterThan,
      isGreaterOrEqual,
      isNameUnique,
    } = useInputValidations();

    const { tkey } = useInternationalization('workpackagePage.scope');

    return {
      canEditWorkpackageScope,
      hasSeasonalityEnabled,
      hasPeriodIdsEnabled,
      betaHasMandatoryPeriodIdsEnabled,
      showSetByEndDateToggle,
      workpackagesStore,
      useMarkdownAndWasteToCalculateMarginAndPriceEnabled,
      defaultFillAccordingTo,
      required,
      isInteger,
      isNumber,
      isLessThan,
      isAlphanumeric,
      isPositive,
      isNotEmpty,
      isGreaterThan,
      isGreaterOrEqual,
      isNameUnique,
      tkey,
    };
  },

  data() {
    return {
      model: {
        fillInSelection: null,
        maximumTrend: null,
        periodIds: [],
        selectedChain: null,
        useMarkdownAndWasteToCalculateMarginAndPrice: false,
        _id: null,
      },
      showSyntheticFillByWarning: false,
      datesLoaded: false,
      isImportingFromPlano: false,
      distinctStoreCount: 0,
      distinctProductCount: 0,
      requiredWorkpackageFields: [
        'performanceStartDate',
        'switchingStartDate',
        'fillInSelection',
        'snapshotDate',
      ],
      selectedTab: 0,
    };
  },

  computed: {
    ...mapState(useWorkpackagesStore, [
      'selectedWorkpackage',
      'wpValidation',
      'wpDateSelections',
      'wpModelSelections',
      'wpSelectedAssortmentGroups',
      'useMarkdownAndWasteToCalculateMarginAndPrice',
    ]),
    ...mapState(useContextStore, ['clientConfig', 'showNotImplemented', 'getDefaultReverseFormat']),
    ...mapState(useWorkpackageProductsStore, ['workpackageProducts']),
    ...mapState(useAssortmentGroupsStore, ['masterAssortmentGroups']),

    workpackage() {
      return this.selectedWorkpackage || {};
    },

    stores() {
      return this.workpackage.stores || [];
    },

    useNewVersionOfAGSelection() {
      return get(this.clientConfig, 'features.useNewVersionOfAGSelection');
    },

    useOldVersionOfAGSelection() {
      return get(this.clientConfig, 'features.useOldVersionOfAGSelection');
    },

    calculateTrendEnabled() {
      return get(this.clientConfig, 'features.calculateTrendEnabled', false);
    },

    defaultMaximumTrend() {
      return get(this.clientConfig, 'maximumTrend');
    },

    tabs() {
      const tabs = [{ id: 'periods', name: this.tkey('tabs.periods') }];

      if (this.useNewVersionOfAGSelection) {
        tabs.push({ id: 'agNew', name: this.tkey('tabs.ag') });
      }

      if (this.useOldVersionOfAGSelection) {
        tabs.push({ id: 'agOld', name: this.tkey('tabs.agOld') });
      }

      return tabs;
    },

    hasSelectedAssortmentGroups() {
      return this.wpSelectedAssortmentGroups.length > 0;
    },

    canRunWPSetup() {
      if (this.isWorkpackageSetupRunning || !this.workpackage) {
        return false;
      }

      return (
        !!this.stores.length &&
        !!this.workpackageProducts.length &&
        this.hasSelectedAssortmentGroups && // TODO: Only force AG if the client is using furniture
        every(this.requiredWorkpackageFields, field => !!this.workpackage[field])
      );
    },

    hasInvalidPeriodIds() {
      return (
        (!this.wpModelSelections.periodIds || this.wpModelSelections.periodIds.length === 0) &&
        this.hasPeriodIdsEnabled &&
        this.betaHasMandatoryPeriodIdsEnabled
      );
    },
  },

  async created() {
    this.initialiseWorkpackageData();
    this.datesLoaded = true;
  },

  methods: {
    ...mapActions(useWorkpackageProductsStore, ['fetchWorkpackageProducts']),
    ...mapActions(useWorkpackagesStore, [
      'updateWpModelSelections',
      'getDatesForSave',
      'getWpDataForSave',
    ]),

    updateModel(val, field) {
      this.updateWpModelSelections({ [field]: val });
    },

    syntheticWarningChangeToProductCountConfirmed() {
      // If the user confirms, we set the fillInSelection to productCount and continue with the setup
      this.showSyntheticFillByWarning = false;
      this.updateWpModelSelections({ fillInSelection: SIZE_TYPES.productCount });
      this.runWpSetup();
    },

    runWpSetup() {
      // Search the masterAssortmentGroups array for the selectedAssortmentGroupSettings
      const selectedAssortmentGroupSettings = this.wpSelectedAssortmentGroups || [];
      const selectedAssortmentGroups = this.masterAssortmentGroups.filter(ag =>
        selectedAssortmentGroupSettings.some(mag => mag.key === ag.key)
      );

      // Check if any of the selectedAssortmentGroups has hasSyntheticFurniture = true
      const hasSyntheticFurniture = selectedAssortmentGroups.some(ag => ag.hasSyntheticFurniture);

      // If we have synthetic furniture and the fillInSelection is not set to productCount then
      // warn the user and ask if they want to change to productCount, if yes we should set and continue, otherwise stop
      if (
        hasSyntheticFurniture &&
        this.wpModelSelections.fillInSelection !== SIZE_TYPES.productCount
      ) {
        this.showSyntheticFillByWarning = true;
        return;
      }

      this.$emit('run-workpackage-setup');
    },

    async initialiseWorkpackageData() {
      this.updateWpModelSelections({
        _id: this.workpackage._id,
        periodIds: this.workpackage.periodIds,
        selectedChain: this.workpackage.selectedChain,
        fillInSelection: this.workpackage.fillInSelection || this.defaultFillAccordingTo,
        useMarkdownAndWasteToCalculateMarginAndPrice:
          this.workpackage.useMarkdownAndWasteToCalculateMarginAndPrice || false,
        linkedPreviousWorkpackageId: this.workpackage.linkedPreviousWorkpackageId,
        linkedPreviousScenarioId: this.workpackage.linkedPreviousScenarioId,
        ...(this.calculateTrendEnabled && {
          maximumTrend: this.workpackage.maximumTrend || this.defaultMaximumTrend,
        }),
      });

      await this.fetchWorkpackageProducts({ workpackageId: this.workpackage._id });
    },

    getData() {
      return this.getWpDataForSave();
    },

    dirtyWorkpackage() {
      return {
        ...this.getData(),
        templateId: this.workpackage.templateId,
      };
    },

    validate() {
      return this.calculateTrendEnabled ? this.validateMaximumTrend() : true;
    },

    validateMaximumTrend() {
      const maximumTrend = this.wpModelSelections.maximumTrend;
      return (
        maximumTrend &&
        this.isNumber(maximumTrend) === true &&
        maximumTrend <= 100 &&
        this.isGreaterOrEqual(maximumTrend, 0) === true
      );
    },

    closeScope() {
      this.$emit('close');
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@style/base/_variables.scss';

.wp-scope {
  background: $assortment-scope-background-colour;
  width: 100%;
  padding-top: 8px;

  &__form {
    padding: 30px 32px;
  }

  :deep(.dates-selection__group-button) {
    flex: 1;

    &:not(.v-btn--active) {
      background-color: $assortment-scope-background-colour !important;
    }
  }

  :deep(.v-select) {
    .v-select__slot {
      background: $assortment-input-background;
      height: 28px !important;
    }

    .v-select__selection {
      padding-left: 5px;
    }

    .v-select__selections {
      input {
        padding-left: 5px;
      }
    }
  }

  :deep(.v-input__slot) {
    background: white;
    height: 30px !important;
  }

  :deep(.v-text-field__slot input) {
    padding-left: 5px;
  }

  :deep(.v-tabs-bar__content :first-child) {
    margin-left: 0px !important;
  }

  :deep(.wp-tab) {
    padding: 0px;
    justify-content: inherit;
    text-align: inherit;
    text-transform: none;
    min-width: 200px;
    border: 1px solid $assortment-tab-divider-colour;
    border-left: 1px solid $assortment-tab-divider-colour;
    font-size: 10px;

    .wp-tab__spacer {
      width: 4px;
      border-bottom: 1px solid $assortment-tab-divider-colour;
    }

    .wp-tab__filler {
      flex-grow: 1;
      border-bottom: 1px solid $assortment-tab-divider-colour;
    }

    .wp-tab__item {
      border-left: 1px solid $assortment-tab-divider-colour;
      border-right: 1px solid $assortment-tab-divider-colour;
    }
  }

  :deep(.theme--light.v-tabs > .v-tabs-bar) {
    background-color: inherit !important;
  }

  :deep(.v-tabs-bar) {
    height: 37px;
  }

  :deep(.v-tab) {
    color: $assortment-primary-colour !important;
  }

  :deep(.v-tab--selected) {
    background-color: $assortment-workpackage-frame-bg-blue;
    color: $assortment-workpackage-text !important;
    border-left: 3px solid $assortment-primary-colour;
    border-bottom: none;
  }

  &__input-heading {
    margin-bottom: 7px;
    font-size: 1.2rem;

    span {
      font-weight: normal;
    }
  }

  &__input-text {
    margin-bottom: 7px;
  }

  p,
  a {
    font-size: 1.2rem;
  }

  .col-padding {
    padding: 0 15px;
  }

  .col-border-left {
    border-left: #ccc 1px solid;
  }

  .search .v-input {
    width: 100% !important;
    padding: 0;
    margin: 0;

    &.div {
      padding: 0;
    }
  }

  .v-select.v-select--chips .v-select__selections {
    min-height: auto !important;
  }
}

.v-card {
  padding-bottom: 0 !important;
}

.footer-panel {
  background-color: $assortment-workpackage-frame-bg-blue;
  border: 1px solid $assortment-tab-divider-colour;
  border-width: 0 1px 1px 1px;

  .btn-text {
    color: $assortment-primary-colour;

    &::before {
      opacity: 0 !important;
    }
  }

  .divider {
    min-height: 20px;
    max-height: 20px;
    margin: auto 0;
  }
}
</style>
